export type SvgBackground = 'white' | 'black' | 'app_light' | 'app_dark' | 'transparent';

const SVG_BACKGROUND_STYLES: Record<SvgBackground, string> = {
  white: '#ffffff',
  black: '#000000',
  app_light: '#f4f4f5',
  app_dark: '#18181b',
  transparent: 'transparent',
};

export function resolveSvgBackgroundColor(bg: SvgBackground | string | undefined): string {
  if (!bg) return SVG_BACKGROUND_STYLES.transparent;
  return SVG_BACKGROUND_STYLES[bg as SvgBackground] ?? SVG_BACKGROUND_STYLES.transparent;
}

export interface StreamingSvgPreviewData {
  title?: string;
  svg: string;
  css?: string;
  background: SvgBackground;
  minHeight: number;
  maxHeight?: number;
}

function isFinitePositiveNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0;
}

export function sanitizePartialSvgForPreview(svg: string): string | null {
  const trimmed = svg.trim();
  if (!trimmed.startsWith('<svg')) {
    return null;
  }

  const hasCompleteOpeningTag = /^<svg\b[^>]*>/i.test(trimmed);
  if (!hasCompleteOpeningTag) {
    return null;
  }

  let safe = trimmed;
  const lastOpen = safe.lastIndexOf('<');
  const lastClose = safe.lastIndexOf('>');

  // Drop a trailing partial tag such as `<rect x="10"` so the browser can
  // still render the completed nodes that arrived before it.
  if (lastOpen > lastClose) {
    safe = safe.slice(0, lastOpen).trimEnd();
  }

  if (!safe.includes('</svg>')) {
    safe = `${safe}</svg>`;
  }

  return safe;
}

export function getStreamingSvgPreview(
  input: Record<string, unknown> | undefined,
): StreamingSvgPreviewData | null {
  const svg = typeof input?.svg === 'string' ? sanitizePartialSvgForPreview(input.svg) : null;

  if (!svg) {
    return null;
  }

  const title = typeof input?.title === 'string' && input.title.trim()
    ? input.title.trim()
    : undefined;
  const css = typeof input?.css === 'string' ? input.css : undefined;
  const validBackgrounds: SvgBackground[] = ['white', 'black', 'app_light', 'app_dark', 'transparent'];
  const background: SvgBackground = input && validBackgrounds.includes(input.background as SvgBackground)
    ? (input.background as SvgBackground)
    : 'transparent';
  const minHeight = isFinitePositiveNumber(input?.minHeight) ? input.minHeight : 260;
  const maxHeight = isFinitePositiveNumber(input?.maxHeight) ? input.maxHeight : undefined;

  return {
    title,
    svg,
    css,
    background,
    minHeight,
    maxHeight,
  };
}
